import { connect } from 'react-redux';
import { useCallback } from 'react';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IErknmKindResultDecisionSelectOption } from 'CorePackage/CatalogsModule/interfaces/IErknmKindResultDecisionSelectOption';
import { KindResultDecisionCodesEnum } from 'APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';
import { IKnmErknmFormNoteLawsuit } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormNoteLawsuit';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionForm';
import { IDecisionResultDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionResultDecisionForm';
import {
  getCurrentDecisionKindCode,
  getDecisionEventHasUnplannedActivity,
  getKnmIsDecisionStatusTypeCompleted,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { IKnmErknmFormViolatedRequirementsNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { KnmResultDecisionStatusEnum } from 'APIPackage/KnmModule/enums/KnmResultDecisionStatusEnum';
import { IDecisionOrganizationForm } from 'DecisionPackage/MainModule/interfaces/IDecisionOrganizationForm';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { DecisionKindCodesEnum } from 'APIPackage/DictionariesModule/enums/DecisionKindCodesEnum';

import { DecisionResultItemContent } from './DecisionResultItemContent/DecisionResultItemContent';
import { DecisionResultItemPaperless } from './DecisionResultItemPaperles/DecisionResultItemPaperless';

interface IDecisionResultProps {
  formRef: IDecisionFormRef | undefined;
  isEnablePaperlessDocument?: boolean;
  isExpandAllInPrescription?: boolean;
  isLater4111ForDecision?: boolean;
  isWatchOnly: boolean;
  name: string;
  organization: IDecisionOrganizationForm | undefined;
  resultDecision: IDecisionResultDecisionForm | undefined;
  resultDecisionId: string;
  resultDecisionIndex: number;
  resultDecisions: IDecisionResultDecisionForm[] | undefined;
  violatedRequirementsNpa: IKnmErknmFormViolatedRequirementsNpa[] | undefined;
}

interface IDecisionResultStoreProps {
  availableUnplannedActivities: boolean | undefined;
  currentDecisionKindCode: string | undefined;
  isDecisionStatusCompleted: boolean;
  isSupervisor: boolean;
}

const DecisionResultWithoutStore = ({
  formRef,
  isEnablePaperlessDocument,
  isExpandAllInPrescription,
  isLater4111ForDecision,
  isWatchOnly,
  name,
  organization,
  resultDecision,
  resultDecisionId,
  resultDecisionIndex,
  resultDecisions,
  violatedRequirementsNpa,
  availableUnplannedActivities,
  currentDecisionKindCode,
  isDecisionStatusCompleted,
  isSupervisor,
}: IDecisionResultProps & IDecisionResultStoreProps) => {
  const isDisableResult = !isSupervisor || !isDecisionStatusCompleted;
  const isDisable = isWatchOnly || isDisableResult || resultDecision?.status === KnmResultDecisionStatusEnum.COMPLETED;
  const isNeedToAddAct: IDecisionForm['isNeedToAddAct'] = formRef?.current.getFieldState('isNeedToAddAct')?.value;

  const removeBatch = formRef?.current.mutators.removeBatch;

  const isPrescription =
    resultDecision?.kind?.code === KindResultDecisionCodesEnum.D_7 ||
    resultDecision?.kind?.code === KindResultDecisionCodesEnum.D_1;

  const handleChangeKind = useCallback(
    (v: IErknmKindResultDecisionSelectOption) => {
      const notesValue = formRef?.current.getFieldState(`${name}.notes` as keyof IDecisionForm)?.value as
        | IKnmErknmFormNoteLawsuit[]
        | undefined;
      const notesLength = notesValue?.length;

      if (notesLength) {
        removeBatch?.(`${name}.notes`, Array.from(Array(notesLength).keys()));
      }

      if (organization?.act?.paperless) {
        if (v?.code === KindResultDecisionCodesEnum.D_7 || v?.code === KindResultDecisionCodesEnum.D_1) {
          formRef?.current?.change(`${name}.paperless` as keyof IDecisionForm, organization?.act?.paperless);
          formRef?.current?.change(
            `${name}.number` as keyof IDecisionForm,
            `${organization?.act?.numberAct}-${resultDecisions?.length}`,
          );
        } else {
          formRef?.current?.change(`${name}.paperless` as keyof IDecisionForm, false);
          formRef?.current?.change(`${name}.number` as keyof IDecisionForm);
        }
      }
    },
    [formRef],
  );

  const getFilterKindResultDecisionsSelectOptions = useCallback(
    (arr: IErknmKindResultDecisionSelectOption[] | undefined) => {
      return arr
        ?.filter(
          kindResultDecisionSelectOption =>
            kindResultDecisionSelectOption.code !== KindResultDecisionCodesEnum.D_6 &&
            kindResultDecisionSelectOption.code !== KindResultDecisionCodesEnum.D_8 &&
            kindResultDecisionSelectOption.code !== KindResultDecisionCodesEnum.D_9 &&
            kindResultDecisionSelectOption.code !== KindResultDecisionCodesEnum.D_10 &&
            ((organization?.act?.paperless &&
              (!organization?.act.familiarizationNotifications ||
                organization?.act.familiarizationNotifications?.length === 0)) ||
            (!availableUnplannedActivities &&
              (currentDecisionKindCode === DecisionKindCodesEnum.VO ||
                currentDecisionKindCode === DecisionKindCodesEnum.NSOT)) ||
            // Для безбумажного варианта не даем выбрать предписание без акта и если нет нарушений ОТ
            (availableUnplannedActivities &&
              isEnablePaperlessDocument &&
              (!organization?.act ||
                !isNeedToAddAct?.value ||
                !organization?.act?.isViolatedMandatoryRequirements?.value ||
                !organization?.act?.violatedMandatoryRequirements?.length) &&
              isEnablePaperlessDocument)
              ? kindResultDecisionSelectOption.code !== KindResultDecisionCodesEnum.D_1 &&
                kindResultDecisionSelectOption.code !== KindResultDecisionCodesEnum.D_7
              : true),
        )
        ?.map(kindResultDecisionSelectOption => ({
          ...kindResultDecisionSelectOption,
          isDisabled: resultDecisions?.some(
            resultDecisionsItem =>
              kindResultDecisionSelectOption.id === resultDecisionsItem.kind?.id &&
              kindResultDecisionSelectOption.id !== resultDecision?.kind?.id &&
              resultDecisionsItem.kind?.code === KindResultDecisionCodesEnum.D_7 &&
              resultDecisionsItem.status !== KnmResultDecisionStatusEnum.CANCELED,
          ),
        }));
    },
    [resultDecisions, resultDecision?.kind, organization, availableUnplannedActivities, isEnablePaperlessDocument],
  );

  return (
    <>
      <Row margin={resultDecision?.paperless && isPrescription ? 'none' : undefined}>
        <FieldBlock
          label={getTitleFieldWithRedStar({
            label: 'Вид решения',
            field: `${name}.kind`,
            isDecision: true,
          })}
        >
          <SelectInputFieldAutoOptions
            name={`${name}.kind`}
            size="medium"
            optionName={CommonCatalogsNamesEnum.ERKNM_KIND_RESULT_DECISION}
            isClearable={false}
            disabled={
              isDisable || !!resultDecision?.signed || (!!resultDecision?.paperless && !!resultDecision.document)
            }
            transformFunc={getFilterKindResultDecisionsSelectOptions}
            onChange={handleChangeKind}
            warning={
              isEnablePaperlessDocument
                ? 'Возможность внесения предписания зависит от значения, выбранного в ЕРВК'
                : undefined
            }
          />
        </FieldBlock>
      </Row>
      {resultDecision?.kind &&
        (resultDecision.paperless && isPrescription ? (
          <DecisionResultItemPaperless
            organization={organization}
            formRef={formRef}
            isWatchOnly={isWatchOnly}
            value={resultDecision}
            name={name}
            violatedRequirementsNpa={organization?.act?.violatedMandatoryRequirements}
            isPaperless={!!resultDecision.paperless}
            resultDecisionId={resultDecisionId}
            resultDecisionIndex={resultDecisionIndex}
            isLater4111ForDecision={isLater4111ForDecision}
            isExpandAllInPrescription={isExpandAllInPrescription}
          />
        ) : (
          <DecisionResultItemContent
            id={resultDecisionId}
            isPrescription={isPrescription}
            formRef={formRef}
            isWatchOnly={isWatchOnly}
            name={name}
            resultDecision={resultDecision}
            resultDecisionIndex={resultDecisionIndex}
            violatedRequirementsNpa={violatedRequirementsNpa}
          />
        ))}
    </>
  );
};

const mapStateToProps = (state: IRootState): IDecisionResultStoreProps => ({
  availableUnplannedActivities: getDecisionEventHasUnplannedActivity(state),
  currentDecisionKindCode: getCurrentDecisionKindCode(state),
  isDecisionStatusCompleted: getKnmIsDecisionStatusTypeCompleted(state),
  isSupervisor: getUserRoleIsSupervisor(state),
});

export const DecisionResult = connect(mapStateToProps)(DecisionResultWithoutStore);
